import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ZoomIn } from "lucide-react";

const ProductGallery = ({ images = [], activeIndex, onActiveIndexChange, alt }) => {
  const activeImage = images?.[activeIndex] || images?.[0];
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  useEffect(() => {
    setZoomed(false);
    setOrigin({ x: 50, y: 50 });
  }, [activeImage]);

  const updateOrigin = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((event.clientX - rect.left) / rect.width) * 100,
      y: ((event.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <div className="md:sticky md:top-24 md:self-start">
      <div
        className={`group relative aspect-[4/5] overflow-hidden bg-cynos-surface md:rounded-[8px] ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
        onPointerMove={updateOrigin}
        onPointerLeave={() => setZoomed(false)}
      >
        {activeImage ? (
          <motion.img
            key={`${activeImage}-${activeIndex}`}
            src={activeImage}
            alt={alt}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, scale: zoomed ? 1.85 : 1 }}
            transition={{ duration: 0.25 }}
            style={{ transformOrigin: `${origin.x}% ${origin.y}%` }}
            className="h-full w-full object-cover object-top"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            sizes="(max-width: 768px) 100vw, 50vw"
            onClick={() => setZoomed((value) => !value)}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-cynos-surface font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
            No image
          </div>
        )}
        <button
          type="button"
          onClick={() => setZoomed((value) => !value)}
          className="absolute bottom-3 right-3 inline-flex min-h-[40px] items-center gap-2 rounded-[8px] border border-white/15 bg-black/70 px-3 font-mono text-[10px] uppercase tracking-[0.16em] text-white backdrop-blur transition hover:border-white"
          aria-label={zoomed ? "Reset image zoom" : "Zoom product image"}
        >
          <ZoomIn size={14} />
          {zoomed ? "Reset" : "Zoom"}
        </button>
      </div>

      {images.length > 1 && (
        <div className="mt-2 grid grid-cols-4 gap-2 px-4 md:px-0">
          {images.map((img, index) => (
            <button
              key={`${img}-${index}`}
              type="button"
              onClick={() => onActiveIndexChange(index)}
              aria-label={`View product image ${index + 1}`}
              aria-pressed={index === activeIndex}
              className={`aspect-[4/5] overflow-hidden rounded-[8px] border transition ${index === activeIndex ? "border-cynos-red" : "border-white/10 hover:border-white/35"}`}
              data-testid={`thumb-${index}`}
            >
              <img src={img} alt="" className="h-full w-full object-cover object-top" loading="lazy" decoding="async" sizes="25vw" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
